import Clarity from "@microsoft/clarity";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate } from "react-router-dom";
import { CLARITY_PROJECT_ID } from "../../../project_config";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import { useAdminGate } from "../../../utils/useAdminGate";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import Footer from "../../common/footer/Footer";
import Header from "../../common/header/Header";
import Paginator from "../../common/paginator/Paginator";
import DashboardSidebar from "../DashboardSidebar";

const inputClassName = "w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 font-Vazir-400 text-[14px] text-darkBlue outline-none transition focus:border-darkBlue/40 focus:ring-2 focus:ring-darkBlue/10";

export default function AdminUsers() {
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [users, setUsers] = useState([]);
	const [name, setName] = useState("");
	const [editingId, setEditingId] = useState(null);
	const [listQuery, setListQuery] = useState("");
	const [appliedQuery, setAppliedQuery] = useState("");
	const [page, setPage] = useState(1);
	const [maxPage, setMaxPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [notice, setNotice] = useState("");
	const listRef = useRef(null);
	const { ready, isAdmin } = useAdminGate();

	useEffect(() => {
		Clarity.init(CLARITY_PROJECT_ID);
		const handleResize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const fetchUsers = useCallback((currentPage = 1, currentQuery = "") => {
		setIsLoading(true);
		setErrorMessage("");
		return api.profileAdminUserList({
			token: LoginHelper.getToken(),
			query: currentQuery,
			page: currentPage,
		}).then((result) => {
			setUsers(result.users || []);
			setPage(result.page || 1);
			setMaxPage(result.max_page || 1);
			setTotalCount(result.total_count || 0);
			return result;
		}).catch((error) => {
			setErrorMessage(error?.data?.message || "دریافت فهرست کاربران ناموفق بود");
			return null;
		}).finally(() => setIsLoading(false));
	}, []);

	useEffect(() => {
		if (isAdmin) fetchUsers(page, appliedQuery);
	}, [isAdmin, page, appliedQuery, fetchUsers]);

	function applySearch(event) {
		event.preventDefault();
		const nextQuery = listQuery.trim();
		if (nextQuery !== appliedQuery) {
			setAppliedQuery(nextQuery);
			setPage(1);
			return;
		}
		fetchUsers(page, appliedQuery);
	}

	function startEditing(user) {
		setEditingId(user.id);
		setName(user.name || "");
		setNotice("");
		setErrorMessage("");
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	function resetForm() {
		setEditingId(null);
		setName("");
	}

	function saveUser(event) {
		event.preventDefault();
		if (isSaving) return;
		setIsSaving(true);
		setErrorMessage("");
		setNotice("");
		api.profileAdminUserSave({
			token: LoginHelper.getToken(),
			...(editingId ? { profile_id: editingId } : {}),
			name: name.trim(),
		}).then(() => {
			setNotice(editingId ? "اطلاعات کاربر به‌روزرسانی شد." : "کاربر جدید با موفقیت ساخته شد.");
			resetForm();
			return fetchUsers(page, appliedQuery);
		}).catch((error) => {
			setErrorMessage(error?.data?.message || "ذخیره اطلاعات کاربر ناموفق بود");
		}).finally(() => setIsSaving(false));
	}

	function removeUser(user) {
		if (!window.confirm(`کاربر «${user.name}» حذف شود؟ این عملیات قابل بازگشت نیست.`)) return;
		setErrorMessage("");
		setNotice("");
		api.profileAdminUserRemove({ token: LoginHelper.getToken(), profile_id: user.id })
			.then(() => {
				setNotice("کاربر حذف شد.");
				if (editingId === user.id) resetForm();
				return fetchUsers(page, appliedQuery);
			})
			.then((result) => {
				if (result && result.users.length === 0 && page > 1) setPage(page - 1);
			})
			.catch((error) => {
				setErrorMessage(error?.data?.message || "حذف کاربر ناموفق بود");
			});
	}

	if (!ready) return null;
	if (!isAdmin) return <Navigate to="/login/" replace />;

	const listSummary = maxPage > 1
		? `${totalCount.toLocaleString("fa-IR")} کاربر · صفحه ${page.toLocaleString("fa-IR")} از ${maxPage.toLocaleString("fa-IR")}`
		: `${totalCount.toLocaleString("fa-IR")} کاربر`;

	return (
		<>
			<Helmet>
				<title>مدیریت کاربران | همراهتیم</title>
				<meta name="description" content="مدیریت کاربران همراهتیم" />
			</Helmet>
			<div className="flex min-h-screen flex-col justify-between bg-[#F0F6FC]">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className="mx-auto max-w-[1200px] px-4">
							<Breadcrumb linkDataList={[
								{ label: "خانه", href: "/" },
								{ label: "داشبورد مدیریت", href: "/dashboard/" },
								{ label: "مدیریت کاربران", href: "/dashboard/users/" },
							]} />
						</div>
					</div>
					<main className={`mx-auto flex w-full max-w-[1200px] gap-5 py-8 ${isDesktop ? "flex-row px-4" : "flex-col p-[10px]"}`}>
						<DashboardSidebar isDesktop={isDesktop} />
						<section className="flex min-w-0 flex-1 flex-col gap-5">
							<div className="rounded-[12px] border border-[#E2E8F0] bg-white p-5 sm:p-4">
								<h1 className="font-Vazir-800 text-[22px] text-darkBlue">مدیریت کاربران</h1>
								<p className="mt-1 font-Vazir-400 text-[13px] text-darkBlue/55">ساخت، ویرایش و مدیریت حساب کاربران عادی سایت</p>
								<form onSubmit={saveUser} className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
									<label className="flex flex-1 flex-col gap-1.5 font-Vazir-500 text-[13px] text-darkBlue">
										نام و نام خانوادگی
										<input required maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={inputClassName} placeholder="نام کاربر" />
									</label>
									<button type="submit" disabled={isSaving} className="rounded-xl bg-cta px-5 py-3 font-Vazir-700 text-[14px] text-white transition hover:bg-cta/90 disabled:opacity-60">
										{isSaving ? "در حال ذخیره…" : editingId ? "ذخیره تغییرات" : "ساخت کاربر"}
									</button>
									{editingId && <button type="button" onClick={resetForm} className="rounded-xl border border-darkBlue/20 px-5 py-3 font-Vazir-600 text-[14px] text-darkBlue">انصراف</button>}
								</form>
								{!editingId && <p className="mt-3 font-Vazir-400 text-[12px] text-darkBlue/50">برای کاربر جدید، شماره تستی با پیشوند 0009 به‌صورت خودکار ساخته می‌شود.</p>}
								{notice && <p role="status" className="mt-3 rounded-xl bg-green-50 px-4 py-3 font-Vazir-500 text-[13px] text-green-800">{notice}</p>}
								{errorMessage && <p role="alert" className="mt-3 rounded-xl bg-red-50 px-4 py-3 font-Vazir-500 text-[13px] text-red-700">{errorMessage}</p>}
							</div>

							<div ref={listRef} className="rounded-[12px] border border-[#E2E8F0] bg-white p-5 sm:p-4">
								<div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
									<div>
										<h2 className="font-Vazir-800 text-[19px] text-darkBlue">فهرست کاربران</h2>
										<p className="mt-1 font-Vazir-400 text-[13px] text-darkBlue/55">{listSummary}{appliedQuery ? ` · جستجو: «${appliedQuery}»` : ""}</p>
									</div>
									<form onSubmit={applySearch} className="flex flex-col gap-2 sm:flex-row">
										<input type="search" value={listQuery} onChange={(event) => setListQuery(event.target.value)} placeholder="جستجو بر اساس نام یا شماره تلفن" className={`${inputClassName} sm:min-w-[260px]`} />
										<button type="submit" disabled={isLoading} className="rounded-xl border border-darkBlue/20 px-5 py-3 font-Vazir-600 text-[13px] text-darkBlue disabled:opacity-60">جستجو</button>
									</form>
								</div>
								<div className={`mt-4 grid gap-3 transition-opacity ${isLoading ? "opacity-60" : ""}`}>
									{users.map((user) => (
										<article key={user.id} className="flex flex-col gap-3 rounded-xl border border-darkBlue/10 p-4 sm:flex-row sm:items-center sm:justify-between">
											<div className="min-w-0">
												<h3 className="font-Vazir-700 text-[15px] text-darkBlue">{user.name || "بدون نام"}</h3>
												<p className="mt-1 font-Vazir-400 text-[12px] text-darkBlue/65" dir="ltr">{user.phone_number || "شماره ثبت نشده"}</p>
											</div>
											<div className="flex shrink-0 gap-2">
												<button type="button" onClick={() => startEditing(user)} className="rounded-lg bg-lightCta px-4 py-2 font-Vazir-600 text-[13px] text-darkBlue">ویرایش</button>
												<button type="button" onClick={() => removeUser(user)} className="rounded-lg border border-red-200 px-4 py-2 font-Vazir-600 text-[13px] text-red-600 hover:bg-red-50">حذف</button>
											</div>
										</article>
									))}
									{!isLoading && users.length === 0 && <p className="py-8 text-center font-Vazir-400 text-[14px] text-midGray">{appliedQuery ? "کاربری با این جستجو پیدا نشد." : "هنوز کاربری ثبت نشده است."}</p>}
								</div>
								{maxPage > 1 && <div className="mt-6 flex justify-center"><Paginator currentPage={page} maxPageNum={maxPage} isLoading={isLoading} onChange={setPage} scrollToTop={false} scrollAnchorRef={listRef} /></div>}
							</div>
						</section>
					</main>
				</div>
				<Footer isDesktop={isDesktop} />
			</div>
		</>
	);
}
